/**
 * Types et constantes pour la gestion des erreurs de paiement.
 *
 * Endpoints concernés :
 *   - POST /order/init/
 *   - POST /order/claim/  (claim init)
 *
 * Les erreurs sont retournées en JSON avec un code HTTP approprié
 * (400, 422, 500, 502). Succès = 200 avec une URL brute.
 */

// ---------------------------------------------------------------------------
// Saferpay behavior – dicte le comportement UX côté front
// ---------------------------------------------------------------------------

export type SaferpayBehavior = 'RETRY' | 'RETRY_LATER' | 'OTHER_MEANS' | 'DO_NOT_RETRY';

export interface SaferpayErrorDetails {
    behavior: SaferpayBehavior;
    errorName: string;
    errorMessage: string;
    errorDetail?: string[];
    isRetryable: boolean;
}

// ---------------------------------------------------------------------------
// Réponse d'erreur générique renvoyée par le backend
// ---------------------------------------------------------------------------

export interface PaymentErrorResponse {
    codeError: string;
    /** Erreurs de validation (serializer) – clé = nom du champ */
    details?: Record<string, string[]>;
    /** Montant invalide (en centimes) */
    amountInCt?: number;
    /** Détails Saferpay */
    saferpay?: SaferpayErrorDetails;
}

// ---------------------------------------------------------------------------
// Codes d'erreur – POST /order/init/
// ---------------------------------------------------------------------------

export const ORDER_INIT_ERRORS = {
    /** Serializer error – champs invalides */
    SERIALIZER_ERROR: 'p_oin_100',
    /** Montant de paiement nul ou invalide */
    INVALID_AMOUNT: 'p_oin_101',
    /** Erreur métier Saferpay (voir saferpay.behavior) */
    SAFERPAY_ERROR: 'p_oin_102',
    /** Erreur inattendue */
    UNEXPECTED_ERROR: 'p_oin_103',
} as const;

// ---------------------------------------------------------------------------
// Codes d'erreur – POST /order/claim/  (claim init)
// ---------------------------------------------------------------------------

export const ORDER_CLAIM_INIT_ERRORS = {
    /** Serializer error – champs invalides */
    SERIALIZER_ERROR: 'p_ocin_100',
    /** Erreur inattendue */
    UNEXPECTED_ERROR: 'p_ocin_101',
    /** Montant de paiement nul ou invalide */
    INVALID_AMOUNT: 'p_ocin_102',
    /** Erreur métier Saferpay (voir saferpay.behavior) */
    SAFERPAY_ERROR: 'p_ocin_103',
} as const;

// ---------------------------------------------------------------------------
// Classe d'erreur custom pour les erreurs de paiement
// ---------------------------------------------------------------------------

export class PaymentError extends Error {
    public readonly data: PaymentErrorResponse;

    constructor(data: PaymentErrorResponse) {
        super(`Payment error: ${data.codeError}`);
        this.name = 'PaymentError';
        this.data = data;
    }
}
